import React, { useEffect, useState } from "react";
import moment from "moment";
import type { TableProps } from "antd";
import { message, Table, Upload } from "antd";
import { filterExam } from "../../redux/apiActions/examActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getCommonState, getExamState } from "../../redux/store/getState";
import { Exam } from "../../redux/apiSlice/examSlice";
import useSearch from "../../hooks/useSearch";
import { EXAM_STATUS } from "../../utils/status";
import { IconPrimary, Loading } from "../../components";
import { UploadIcon } from "../../assets";
import axiosClient from "../../api/axiosClient";
import { getUserSubmittedExamInstances } from "../../redux/apiActions/examinerActions";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const MyExamTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { userExams, loading } = useAppSelector(getExamState);
  const { filterFrom, filterTo } = useAppSelector(getCommonState);
  const [data, setData] = useState<Exam[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [fileLoading, setLoading] = useState(false);

  useSearch(userExams, setData);

  useEffect(() => {
    if (filterFrom && filterTo) {
      dispatch(filterExam({ from: filterFrom, to: filterTo }));
    } else {
      dispatch(getUserSubmittedExamInstances());
    }
  }, [filterFrom, filterTo, dispatch]);

  const handleCustomRequest = async ({ file, onSuccess, onError, id }: any) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      setLoading(true);
      const response = await axiosClient.post(
        "/files/exam_report/upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const uploadedFileUrl = response.data.url;

      await axiosClient.post(`examinstances/${id}/submitreport`, {
        url: uploadedFileUrl,
      });

      onSuccess(response.data);

      message.success(`Report uploaded successfully`);
      dispatch(getUserSubmittedExamInstances());
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Exam> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },

    {
      title: "Exam Name",
      dataIndex: "exam_name",
      render: (exam_name) => <>{exam_name}</>,
    },

    {
      title: "Action",
      render: (_, record) => (
        <IconPrimary disabled={record.status !== "report_pending"}>
          <Upload
            customRequest={(params) =>
              handleCustomRequest({ ...params, id: record.id })
            }
            listType="picture"
            maxCount={1}
            showUploadList={false}
            fileList={[]}
          >
            <UploadIcon style={{ marginTop: 7 }} />
          </Upload>
        </IconPrimary>
      ),
    },

    {
      title: "Exam Status",
      dataIndex: "status",
      render: (status) => <>{EXAM_STATUS[status] || status}</>,
    },
  ];

  return (
    <>
      <Table
        scroll={{ x: 300 }}
        style={{ marginTop: 20, whiteSpace: "pre", width: "100%" }}
        loading={loading.getExam}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data}
        onChange={(p) =>
          setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
        }
        pagination={pagination}
        rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
      />
      {fileLoading ? <Loading /> : null}
    </>
  );
};

export default MyExamTable;
